/* ETCH motion layer
   - scroll-velocity "drag blur" on media (the fluid/alive feel)
   - progressive-blur boundaries injected at dark section seams
   - IntersectionObserver reveal for headings, cards, media
   - chapter pill naming the section in view
   All GPU/paint-cheap, rAF-batched, reduced-motion safe. */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESKTOP = window.matchMedia('(min-width: 990px)').matches;

  /* ------------------------------------------------ 1. reveal on enter */
  function reveal() {
    var groups = [
      ['.title-wrapper-with-link .title', 0],
      ['.collection-hero__text-wrapper', 0],
      ['.card-wrapper', 1],
      ['.image-with-text__content > *', 1],
      ['.multicolumn-list__item', 1],
      ['.etch-shader-hero__content', 0]
    ];
    var seen = [];
    groups.forEach(function (g) {
      var nodes = document.querySelectorAll(g[0]);
      Array.prototype.forEach.call(nodes, function (el, i) {
        if (el.classList.contains('etch-reveal')) return;
        el.classList.add('etch-reveal');
        if (g[1]) {
            var d = Math.min(i % 4, 3) + 1;
            el.classList.add('etch-reveal--d' + d);
        }
        seen.push(el);
      });
    });
    if (!('IntersectionObserver' in window) || REDUCED) {
      seen.forEach(function (el) { el.classList.add('etch-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('etch-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    seen.forEach(function (el) { io.observe(el); });
  }

  /* --------------------------------- 2. scroll-velocity blur ("drag") */
  function velocityBlur() {
    if (REDUCED) return;
    var media = document.querySelectorAll('.card__media, .image-with-text__media, .banner__media, .collection-hero__image-container');
    if (!media.length) return;
    Array.prototype.forEach.call(media, function (m) { m.classList.add('etch-vblur'); });

    var lastY = window.scrollY;
    var lastT = performance.now();
    var blur = 0;
    var ticking = false;

    function frame() {
      ticking = false;
      var now = performance.now();
      var dy = Math.abs(window.scrollY - lastY);
      var dt = Math.max(1, now - lastT);
      lastY = window.scrollY;
      lastT = now;
      var v = dy / dt;                       // px per ms
      var target = Math.min(2.6, v * 1.5);   // cap so it never smears illegibly
      blur += (target - blur) * 0.25;
      if (blur < 0.02) blur = 0;
      document.documentElement.style.setProperty('--etch-vblur', blur.toFixed(2));
      if (blur > 0.02) { ticking = true; requestAnimationFrame(frame); }
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(frame); }
    }, { passive: true });
  }

  /* -------------------------- 3. progressive blur at section boundaries */
  function progressiveBoundaries() {
    if (!DESKTOP || REDUCED) return;
    if (!CSS.supports('backdrop-filter', 'blur(4px)') &&
        !CSS.supports('-webkit-backdrop-filter', 'blur(4px)')) return;
    var targets = document.querySelectorAll('[id*="__image_with_text"], [id*="EtchShaderHero"], [id*="EtchSignalHero"]');
    Array.prototype.forEach.call(targets, function (sec) {
      if (sec.querySelector('.etch-progressive')) return;
      if (getComputedStyle(sec).position === 'static') sec.style.position = 'relative';
      var d = document.createElement('div');
      d.className = 'etch-progressive etch-progressive--bottom';
      d.setAttribute('aria-hidden', 'true');
      d.innerHTML = '<i></i><i></i><i></i><i></i>';
      sec.appendChild(d);
    });
  }

  /* ------------------------------------------------------ 4. chapter pill */
  function chapterPill() {
    if (!DESKTOP || REDUCED) return;
    var MAP = [
      ['EtchSignalHero', 'ETCH'],
      ['EtchShaderHero', 'ETCH'],
      ['__featured_collection', 'Engravable'],
      ['__image_with_text', 'Marked, not made'],
      ['__featured_essentials', 'Essentials'],
      ['__multicolumn_NqcBqR', 'Everyday by design'],
      ['__multicolumn_image_with_link', 'Categories']
    ];
    var targets = [];
    MAP.forEach(function (m) {
      var el = document.querySelector('[id*="' + m[0] + '"]');
      if (el) targets.push({ el: el, label: m[1] });
    });
    if (targets.length < 2) return;

    var pill = document.createElement('div');
    pill.className = 'etch-pill';
    pill.setAttribute('aria-hidden', 'true');
    pill.innerHTML = '<span class="etch-pill__dot"></span><span class="etch-pill__label">ETCH</span>';
    document.body.appendChild(pill);
    var labelEl = pill.querySelector('.etch-pill__label');
    var current = '';
    function setLabel(text) {
      if (text === current) return;
      current = text;
      labelEl.style.opacity = '0';
      setTimeout(function () { labelEl.textContent = text; labelEl.style.opacity = '1'; }, 140);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var t = targets.filter(function (x) { return x.el === e.target; })[0];
          if (t) setLabel(t.label);
        }
      });
    }, { rootMargin: '-42% 0px -42% 0px' });
    targets.forEach(function (t) { io.observe(t.el); });

    window.addEventListener('scroll', function () {
      pill.classList.toggle('etch-pill--visible', window.scrollY > 320);
    }, { passive: true });
  }

  function init() {
    reveal();
    velocityBlur();
    progressiveBoundaries();
    chapterPill();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
